import React from "react";
import {  Zap, RotateCcw } from "lucide-react";

interface ExecutionTimelineProps {
  executions: any[];
  selectedExecutionId: string | null;
  onSelectExecution: (id: string) => void;
}

const fmtDate = (d: string) => {
  const date = new Date(d);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const time = date.toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return `${months[date.getMonth()]} ${date.getDate()}, ${time}`;
};

const fmtDuration = (s?: number) => {
  if (s === undefined || s === null) return "";
  if (s < 1) return `${(s * 1000).toFixed(0)}ms`;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

const fmtTokens = (n: number) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : String(n);

const STATUS: Record<string, { bar: string; text: string; label: string }> = {
  DONE:        { bar: "bg-emerald-500", text: "text-emerald-600", label: "Succeeded" },
  FAILED:      { bar: "bg-red-500",     text: "text-red-500",     label: "Error" },
  ERROR:       { bar: "bg-red-500",     text: "text-red-500",     label: "Error" },
  WARNING:     { bar: "bg-amber-500",   text: "text-amber-600",   label: "Warning" },
  IN_PROGRESS: { bar: "bg-blue-500",    text: "text-blue-500",    label: "Running" },
  RUNNING:     { bar: "bg-blue-500",    text: "text-blue-500",    label: "Running" },
};
const DEFAULT = { bar: "bg-gray-400", text: "text-gray-400", label: "Unknown" };

export const ExecutionTimeline: React.FC<ExecutionTimelineProps> = ({
  executions,
  selectedExecutionId,
  onSelectExecution,
}) => {
  if (!executions.length) return null;

  return (
    <div className="flex flex-col">
      {executions.map((exec) => {
        const s = STATUS[exec.overall_proccessing_status] || DEFAULT;
        const isSelected = selectedExecutionId === exec._id;
        const dur = exec.overall_proccessing?.time_taken;
        const steps = exec.total_steps || exec.node_status?.length || 0;
        const retryCount = exec.retry_count || 0;
        const tokens =
          Array.isArray(exec.token_usages) && exec.token_usages.length > 0
            ? exec.token_usages.reduce((a: number, u: any) => a + (u.total_tokens || 0), 0)
            : 0;
        const failedAt = exec.node_status?.find(
          (ns: any) => ns.status === "FAILED" || ns.status === "ERROR",
        )?.node_name;

        return (
          <div
            key={exec._id}
            onClick={() => onSelectExecution(exec._id)}
            className={`group flex border-b border-gray-100 cursor-pointer transition-colors ${
              isSelected ? "bg-gray-100/80" : "hover:bg-gray-50/80"
            }`}
          >
            {/* Status bar */}
            <div className={`w-[3px] shrink-0 ${s.bar}`} />

            {/* Content */}
            <div className="flex-1 flex items-center justify-between gap-3 px-3 py-2.5 min-w-0">
              <div className="min-w-0">
                <p className="text-xs text-gray-500 leading-none">
                  {fmtDate(exec.createdAt)}
                </p>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <span className={`text-xs font-semibold ${s.text}`}>{s.label}</span>
                  {dur != null && (
                    <span className="text-[11px] text-gray-400">in {fmtDuration(dur)}</span>
                  )}
                </div>
                {failedAt && (
                  <p className="text-[10px] text-red-400 mt-0.5 truncate">
                    at <span className="font-medium">{failedAt}</span>
                  </p>
                )}
                {retryCount > 0 && (
                  <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-0.5">
                    <RotateCcw className="w-3 h-3" /> retry #{retryCount}
                  </p>
                )}
              </div>

              {/* Right: metrics */}
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-[10px] text-gray-400">{steps} steps</span>
                {tokens > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">
                    <Zap className="w-2.5 h-2.5" />
                    {fmtTokens(tokens)}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExecutionTimeline;
